import { useMutation, useQuery, useInfiniteQuery } from '@tanstack/react-query'
import axios from 'axios'
import { API_URL } from '../config'
import type { CognitiveBias } from '../types'
// Import the static bias data
import staticBiasData from '../data/biasData'

interface ContactFormData {
  name: string
  email: string
  subject: string
  message: string
}

interface BiasesResponse {
  biases: CognitiveBias[];
  nextPage?: number;
  total: number;
  currentPage: number;
}

// Helper to check if we're in prerendering mode
const isPrerenderMode = () => 
  typeof window !== 'undefined' && 
  ((window as any).__PRERENDER_INJECTED || 
   (window as any).__PRERENDERED || 
   navigator.userAgent.indexOf('Prerender') !== -1);

// Get the appropriate API URL for the current environment
const getApiUrl = () => {
  // During pre-rendering, we need to use an absolute URL
  if (isPrerenderMode()) {
    // If we're in production pre-rendering, use the production API
    if (process.env.NODE_ENV === 'production') {
      return 'https://www.cognitivebiaslab.com';
    }
    // For development pre-rendering, use the local API
    return 'http://localhost:3001';
  }
  
  // For normal runtime, use the configured API_URL
  return API_URL;
};

// Fetch paginated biases with basic info
export const useBiases = () => {
  const prerendering = isPrerenderMode();
  const apiUrl = getApiUrl();

  return useInfiniteQuery<BiasesResponse>({
    queryKey: ['biases'],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      // If prerendering, use static data
      if (prerendering) {
        console.log('[Prerender] Using static bias data from biasData.ts');
        
        // Create a response using the static data
        return {
          biases: staticBiasData.biases,
          nextPage: undefined,
          total: staticBiasData.total,
          currentPage: 1
        } as BiasesResponse;
      }
      
      // For client-side rendering, use the API
      console.log(`[Client] Fetching biases from ${apiUrl}/api/biases?page=${pageParam}`);
      
      try {
        const { data } = await axios.get<BiasesResponse>(
          `${apiUrl}/api/biases?page=${pageParam}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            timeout: 10000
          }
        );
        
        console.log(`[Client] Successfully fetched ${data.biases.length} biases`);
        return data;
      } catch (error) {
        console.error(`[Client] Error fetching biases:`, error);
        throw error;
      }
    },
    getNextPageParam: (lastPage: BiasesResponse) => lastPage.nextPage,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 3,
    networkMode: 'always',
    refetchOnMount: true,
  });
}

// Fetch single bias with full details
export function useBiasDetails(biasId: string | undefined) {
  const prerendering = isPrerenderMode();
  const apiUrl = getApiUrl();

  return useQuery<CognitiveBias | null, Error>({
    queryKey: ['bias', biasId],
    queryFn: async () => {
      if (!biasId) return null;
      
      // If prerendering, use static data
      if (prerendering) {
        console.log(`[Prerender] Using static data for bias: ${biasId}`);
        
        // Find the bias in the static data
        const bias = staticBiasData.biases.find((b: CognitiveBias) => b.id === biasId);
        
        if (bias) {
          console.log(`[Prerender] Found bias in static data: ${bias.name}`);
          
          // Dispatch an event to signal that bias data has been loaded (for pre-rendering)
          if (typeof document !== 'undefined') {
            console.log(`[Prerender] Preparing to dispatch bias-data-loaded event for ${biasId}`);
            
            // Add a delay to ensure React has time to update the meta tags
            setTimeout(() => {
              console.log(`[Prerender] Dispatching bias-data-loaded event for ${biasId} after delay`);
              document.dispatchEvent(new Event('bias-data-loaded'));
            }, 2000); // 2 second delay
          }
          
          return bias;
        } else {
          console.log(`[Prerender] Bias not found in static data: ${biasId}`);
          return null;
        }
      }
      
      // For client-side rendering, use the API
      console.log(`[Client] Fetching bias details for ${biasId}`);
      
      try {
        const { data } = await axios.get<CognitiveBias>(
          `${apiUrl}/api/biases/${biasId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            timeout: 10000
          }
        );
        
        console.log(`[Client] Successfully fetched bias details for ${biasId}`);
        return data;
      } catch (error) {
        console.error(`[Client] Error fetching bias details:`, error);
        throw error;
      }
    },
    enabled: !!biasId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 3,
  });
}

// Map of simulation IDs to their folder names
const simulationPaths: Record<string, string> = {
  'AnchoringBiasSimulation': 'anchoring',
  'GamblersFallacySimulation': 'gamblers-fallacy',
  'SunkCostFallacySimulation': 'sunk-cost',
  'ConfirmationBiasSimulation': 'confirmation-bias',
  'RosyRetrospectionSimulation': 'rosy-retrospection',
  'StatusQuoBiasSimulation': 'status-quo'
}

// Load simulation component
export function useSimulation(simulationId: string | undefined) {
  return useQuery({
    queryKey: ['simulation', simulationId],
    queryFn: async () => {
      if (!simulationId) return null

      const folderName = simulationPaths[simulationId]
      if (!folderName) {
        console.error('No matching simulation folder for:', simulationId)
        return null
      }

      // Dynamic import based on simulation folder
      try {
        const module = await import(`../components/simulations/${folderName}/index.tsx`)
        return module.default
      } catch (error) {
        console.error('Error loading simulation:', error)
        return null
      }
    },
    enabled: !!simulationId, // Only run query if simulationId is provided
    staleTime: Infinity, // Once loaded, keep it forever
    gcTime: 30 * 60 * 1000, // Cache for 30 minutes
  })
}

// Submit contact form
export function useContactForm() {
  const apiUrl = getApiUrl();
  
  return useMutation({
    mutationFn: async (formData: ContactFormData) => {
      const { data } = await axios.post(
        `${apiUrl}/api/contact`,
        formData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      return data;
    }
  });
} 